import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import {
  Plus,
  AlertCircle,
  Send,
  RefreshCw,
  Lightbulb,
  FileText,
  X,
  Settings,
  Save,
  ChevronDown,
} from "lucide-react";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";

const AddNewQuestion = () => {
  const [showSettings, setShowSettings] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);

  // Use React Hook Form for everything
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      question: "",
      answer: "",
      options: ["", "", "", ""],
      explanation: "",
      // Settings fields
      subjectId: "",
      chapterId: "",
      topicId: "",
      difficulty: "medium",
      tags: [],
      relevantExams: [],
      year: new Date().getFullYear(),
    },
  });

  // Watch settings fields
  const watchedSubjectId = watch("subjectId");
  const watchedChapterId = watch("chapterId");

  // Fetch subjects on component mount
  useEffect(() => {
    fetchSubjects();

    // Load saved settings from localStorage
    const savedSettings = localStorage.getItem("questionSettings");
    if (savedSettings) {
      const parsedSettings = JSON.parse(savedSettings);
      Object.keys(parsedSettings).forEach((key) => {
        setValue(key, parsedSettings[key]);
      });
    }
  }, [setValue]);

  // Fetch chapters when subject changes
  useEffect(() => {
    if (watchedSubjectId) {
      fetchChapters(watchedSubjectId);
    } else {
      setChapters([]);
      setValue("chapterId", "");
      setValue("topicId", "");
    }
  }, [watchedSubjectId, setValue]);

  // Fetch topics when chapter changes
  useEffect(() => {
    if (watchedSubjectId && watchedChapterId) {
      fetchTopics(watchedSubjectId, watchedChapterId);
    } else {
      setTopics([]);
      setValue("topicId", "");
    }
  }, [watchedSubjectId, watchedChapterId, setValue]);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    const settings = {
      subjectId: getValues("subjectId"),
      chapterId: getValues("chapterId"),
      topicId: getValues("topicId"),
      difficulty: getValues("difficulty"),
      tags: getValues("tags"),
      relevantExams: getValues("relevantExams"),
      year: getValues("year"),
    };
    localStorage.setItem("questionSettings", JSON.stringify(settings));
  }, [getValues, watchedSubjectId, watchedChapterId]);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/subject/all`, {
        params: { pageSize: 100 },
      });
      setSubjects(data.data || []);
    } catch (error) {
      toast.error("Failed to fetch subjects");
      console.error("Error fetching subjects:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChapters = async (subjectId) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/chapter/all`, {
        params: { subjectId, pageSize: 100 },
      });
      setChapters(data.data || []);
    } catch (error) {
      toast.error("Failed to fetch chapters");
      console.error("Error fetching chapters:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTopics = async (subjectId, chapterId) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/topics/all`, {
        params: { subjectId, chapterId, pageSize: 100 },
      });
      setTopics(data.data || []);
    } catch (error) {
      toast.error("Failed to fetch topics");
      console.error("Error fetching topics:", error);
    } finally {
      setLoading(false);
    }
  };

  const difficultyOptions = [
    { value: "easy", label: "Easy", color: "green", level: "Beginner" },
    { value: "medium", label: "Medium", color: "blue", level: "Intermediate" },
    { value: "hard", label: "Hard", color: "orange", level: "Advanced" },
    { value: "expert", label: "Expert", color: "red", level: "Expert" },
  ];

  const examOptions = [
    "JEE Main",
    "JEE Advanced",
    "NEET",
    "UPSC",
    "CAT",
    "GATE",
    "SSC",
    "Bank PO",
    "CLAT",
    "NDA",
  ];

  const onSubmit = async (data) => {
    try {
      // Filter out empty options
      const filteredOptions = data.options.filter(
        (option) => option.trim() !== ""
      );

      const payload = {
        ...data,
        options: filteredOptions,
        year: parseInt(data.year),
        tags: Array.isArray(data.tags)
          ? data.tags
          : [data.tags].filter(Boolean),
        relevantExams: Array.isArray(data.relevantExams)
          ? data.relevantExams
          : [data.relevantExams].filter(Boolean),
      };

      const { data: resData } = await axios.post(
        `${server}/question/new`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success("Question created successfully!");

      // Reset only the question-specific fields, keep settings
      const currentSettings = {
        subjectId: getValues("subjectId")
          ? Number(getValues("subjectId"))
          : null,
        chapterId: getValues("chapterId")
          ? Number(getValues("chapterId"))
          : null,
        topicId: getValues("topicId") ? Number(getValues("topicId")) : null,
        difficulty: getValues("difficulty"),
        tags: getValues("tags"),
        relevantExams: getValues("relevantExams"),
        year: getValues("year") ? Number(getValues("year")) : null,
      };

      reset({
        question: "",
        answer: "",
        options: ["", "", "", ""],
        explanation: "",
        ...currentSettings,
      });
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to create question"
      );
    }
  };

  const addOption = () => {
    const currentOptions = watch("options");
    if (currentOptions.length < 6) {
      setValue("options", [...currentOptions, ""]);
    }
  };

  const removeOption = (index) => {
    const currentOptions = watch("options");
    if (currentOptions.length > 2) {
      setValue(
        "options",
        currentOptions.filter((_, i) => i !== index)
      );
    }
  };

  const updateOption = (index, value) => {
    const currentOptions = watch("options");
    const newOptions = [...currentOptions];
    newOptions[index] = value;
    setValue("options", newOptions);
  };

  const resetSettings = () => {
    const defaultSettings = {
      subjectId: "",
      chapterId: "",
      topicId: "",
      difficulty: "medium",
      tags: [],
      relevantExams: [],
      year: new Date().getFullYear(),
    };

    // Reset settings in form
    Object.keys(defaultSettings).forEach((key) => {
      setValue(key, defaultSettings[key]);
    });

    toast.success("Settings reset to default!");
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find((s) => s.id === parseInt(subjectId));
    return subject ? subject.subject : "Unknown Subject";
  };

  const getChapterName = (chapterId) => {
    const chapter = chapters.find((c) => c.id === parseInt(chapterId));
    return chapter ? chapter.chapter : "Unknown Chapter";
  };

  const getTopicName = (topicId) => {
    const topic = topics.find((t) => t.id === parseInt(topicId));
    return topic ? topic.topic : "Unknown Topic";
  };

  const SettingsPanel = ({ onClose, onReset }) => {
    const currentSettings = {
      subjectId: watch("subjectId"),
      chapterId: watch("chapterId"),
      topicId: watch("topicId"),
      difficulty: watch("difficulty"),
      tags: watch("tags"),
      relevantExams: watch("relevantExams"),
      year: watch("year"),
    };

    const handleSubjectChange = (e) => {
      const newSubjectId = e.target.value;
      setValue("subjectId", newSubjectId);
      setValue("chapterId", "");
      setValue("topicId", "");
    };

    const handleChapterChange = (e) => {
      const newChapterId = e.target.value;
      setValue("chapterId", newChapterId);
      setValue("topicId", "");
    };

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 overflow-y-auto"
      >
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="bg-gray-800 rounded-2xl p-6 max-w-2xl w-full my-8 border border-gray-700"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <Settings className="w-6 h-6 text-blue-400" />
              Question Settings
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Subject */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Subject *
                </label>
                <div className="relative">
                  <select
                    value={currentSettings.subjectId}
                    onChange={handleSubjectChange}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                  >
                    <option value="">Select Subject</option>
                    {subjects.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.subject}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>

              {/* Chapter */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Chapter
                </label>
                <div className="relative">
                  <select
                    value={currentSettings.chapterId}
                    onChange={handleChapterChange}
                    disabled={!currentSettings.subjectId}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Select Chapter</option>
                    {chapters.map((chapter) => (
                      <option key={chapter.id} value={chapter.id}>
                        {chapter.chapter}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>

              {/* Topic */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Topic
                </label>
                <div className="relative">
                  <select
                    value={currentSettings.topicId}
                    onChange={(e) => setValue("topicId", e.target.value)}
                    disabled={
                      !currentSettings.subjectId || !currentSettings.chapterId
                    }
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Select Topic</option>
                    {topics.map((topic) => (
                      <option key={topic.id} value={topic.id}>
                        {topic.topic}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>

              {/* Difficulty */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Difficulty
                </label>
                <select
                  value={currentSettings.difficulty}
                  onChange={(e) => setValue("difficulty", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {difficultyOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Year
                </label>
                <input
                  type="number"
                  value={currentSettings.year}
                  onChange={(e) =>
                    setValue(
                      "year",
                      parseInt(e.target.value) || new Date().getFullYear()
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Relevant Exams */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Relevant Exams
              </label>
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto p-2 bg-gray-700 rounded-lg">
                {examOptions.map((exam) => (
                  <label key={exam} className="flex items-center gap-2 p-1">
                    <input
                      type="checkbox"
                      checked={
                        currentSettings.relevantExams?.includes(exam) || false
                      }
                      onChange={(e) => {
                        const currentExams =
                          currentSettings.relevantExams || [];
                        if (e.target.checked) {
                          setValue("relevantExams", [...currentExams, exam]);
                        } else {
                          setValue(
                            "relevantExams",
                            currentExams.filter((e) => e !== exam)
                          );
                        }
                      }}
                      className="w-6 rounded border-gray-600 bg-gray-600"
                    />
                    <span className="text-sm text-gray-300">{exam}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t border-gray-700">
              <button
                type="button"
                onClick={onReset}
                className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Reset to Default
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Save className="w-4 h-4" />
                Save & Close
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  return (
    <motion.div
      className="max-w-4xl mx-auto p-6 space-y-8 bg-gray-900 min-h-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header with Settings Button */}
      <div className="text-center space-y-3">
        <div className="flex items-center justify-center gap-3">
          <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-lg shadow-blue-500/20">
            <Plus className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-br from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Add New Question
          </h1>
        </div>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          Create comprehensive questions with persistent settings for efficient
          bulk uploads.
        </p>

        {/* Settings Button */}
        <button
          type="button"
          onClick={() => setShowSettings(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors mx-auto"
        >
          <Settings className="w-4 h-4" />
          Configure Settings
        </button>

        {/* Current Settings Preview */}
        <div className="flex flex-wrap gap-2 justify-center">
          {watch("subjectId") && (
            <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">
              Subject: {getSubjectName(watch("subjectId"))}
            </span>
          )}
          {watch("chapterId") && (
            <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">
              Chapter: {getChapterName(watch("chapterId"))}
            </span>
          )}
          {watch("topicId") && (
            <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">
              Topic: {getTopicName(watch("topicId"))}
            </span>
          )}
          {watch("difficulty") && (
            <span className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm">
              {watch("difficulty")}
            </span>
          )}
          {watch("relevantExams")?.length > 0 && (
            <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm">
              {watch("relevantExams").length} exams
            </span>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Question Filters */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-lg font-semibold text-white mb-4">
            Question Filters
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Subject Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Subject *
              </label>
              <div className="relative">
                <select
                  value={watch("subjectId")}
                  onChange={(e) => {
                    setValue("subjectId", e.target.value);
                    setValue("chapterId", "");
                    setValue("topicId", "");
                  }}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors appearance-none"
                >
                  <option value="">Select Subject</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.subject}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              </div>
            </div>

            {/* Chapter Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Chapter
              </label>
              <div className="relative">
                <select
                  value={watch("chapterId")}
                  onChange={(e) => {
                    setValue("chapterId", e.target.value);
                    setValue("topicId", "");
                  }}
                  disabled={!watch("subjectId")}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select Chapter</option>
                  {chapters.map((chapter) => (
                    <option key={chapter.id} value={chapter.id}>
                      {chapter.chapter}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              </div>
            </div>

            {/* Topic Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Topic
              </label>
              <div className="relative">
                <select
                  value={watch("topicId")}
                  onChange={(e) => setValue("topicId", e.target.value)}
                  disabled={!watch("subjectId") || !watch("chapterId")}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select Topic</option>
                  {topics.map((topic) => (
                    <option key={topic.id} value={topic.id}>
                      {topic.topic}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Question & Options Section */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <FileText className="w-5 h-5 text-blue-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">
              Question Content
            </h2>
          </div>

          {/* Question */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Question *
            </label>
            <textarea
              className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 resize-none bg-gray-800 text-white placeholder-gray-500"
              rows="4"
              placeholder="Enter your question here..."
              {...register("question", { required: "Question is required" })}
            />
            {errors.question && (
              <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.question.message}
              </p>
            )}
          </div>

          {/* Options */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Options (Minimum 2 required) *
            </label>
            {watch("options").map((option, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="flex items-center justify-center w-6 h-6 bg-gray-700 text-gray-300 rounded-lg font-semibold text-sm">
                  {String.fromCharCode(65 + index)}
                </div>
                <input
                  type="text"
                  className="flex-1 px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 bg-gray-800 text-white placeholder-gray-500"
                  placeholder={`Option ${String.fromCharCode(65 + index)}`}
                  value={option}
                  onChange={(e) => updateOption(index, e.target.value)}
                />
                {watch("options").length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeOption(index)}
                    className="p-3 text-red-400 hover:bg-red-500/10 rounded-xl transition-all duration-200"
                  >
                    <AlertCircle className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
            {watch("options").length < 6 && (
              <button
                type="button"
                onClick={addOption}
                className="flex items-center gap-2 px-4 py-3 text-blue-400 hover:bg-blue-500/10 rounded-xl transition-all duration-200 font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Option
              </button>
            )}
          </div>

          {/* Correct Answer */}
          <div className="mt-6">
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Correct Answer *
            </label>
            <select
              className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 bg-gray-800 text-white"
              {...register("answer", {
                required: "Correct answer is required",
              })}
            >
              <option value="">Select Correct Option</option>
              {watch("options").map(
                (option, index) =>
                  option.trim() && (
                    <option key={index} value={option}>
                      {String.fromCharCode(65 + index)}: {option}
                    </option>
                  )
              )}
            </select>
            {errors.answer && (
              <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.answer.message}
              </p>
            )}
          </div>
        </div>

        {/* Explanations Section */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <Lightbulb className="w-5 h-5 text-green-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">Explanations</h2>
          </div>

          <div className="space-y-6">
            {/* Explanation */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Explanation
              </label>
              <textarea
                className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 resize-none bg-gray-800 text-white placeholder-gray-500"
                rows="3"
                placeholder="Detailed explanation of the answer..."
                {...register("explanation")}
              />
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t border-gray-700">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-br from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Creating Question...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Create Question
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => {
              const currentSettings = {
                subjectId: watch("subjectId"),
                chapterId: watch("chapterId"),
                topicId: watch("topicId"),
                difficulty: watch("difficulty"),
                tags: watch("tags"),
                relevantExams: watch("relevantExams"),
                year: watch("year"),
              };

              reset({
                question: "",
                answer: "",
                options: ["", "", "", ""],
                hint: "",
                explanation: "",
                solution: "",
                ...currentSettings,
              });
            }}
            className="flex items-center justify-center gap-2 px-8 py-4 border-2 border-gray-600 text-gray-300 font-semibold rounded-xl hover:border-gray-500 hover:bg-gray-800 transition-all duration-200"
          >
            <RefreshCw className="w-5 h-5" />
            Reset Form
          </button>
        </div>
      </form>

      {/* Settings Modal */}
      {showSettings && (
        <SettingsPanel
          onClose={() => setShowSettings(false)}
          onReset={resetSettings}
        />
      )}
    </motion.div>
  );
};

export default AddNewQuestion;
